import styled from "styled-components"

import { ProfileIcon } from "@minuthq/meatball-ui-icons"
import { colors, spacing, Text } from "@minuthq/meatball-ui-react"

import { Pager } from "src/components/Pager/Pager"
import { useUrlPager } from "src/components/Pager/useUrlPager"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useFetchMembers } from "src/data/organizations/queries/memberQueries"
import { IOrganizationMember } from "src/data/organizations/types/organizationMemberTypes"
import { useFetchTaskActivityLog } from "src/data/taskManagement/taskManagementQueries"
import { TTaskEvent } from "src/data/taskManagement/taskManagementTypes"
import { langKeys } from "src/i18n/langKeys"
import { TTranslateFunction, useTranslate } from "src/i18n/useTranslate"
import { GridTable } from "src/ui/GridTable/GridTable"
import {
  TableColumn,
  useTableColumns,
} from "src/ui/GridTable/useTableColumns/useTableColumns"
import MinutIcon from "src/ui/icons/minut-roof.svg"
import { LocalizedDate } from "src/ui/LocalizedDate/LocalizedDate"

export function TaskActivityLog({
  taskId,
  timezone,
  homeId,
}: {
  taskId: string
  timezone: string
  homeId: string
}) {
  const { t, langKeys } = useTranslate()
  const { orgId } = useOrganization()

  const { limit, offset, setOffset } = useUrlPager({
    initialOffset: 0,
    initialLimit: 10,
  })

  const fetchTaskActivityLog = useFetchTaskActivityLog({
    orgId,
    taskId,
    filters: {
      limit,
      offset,
      sort_by: "occurred_at",
      sort_order: "desc",
    },
  })

  const taskActivityLog = fetchTaskActivityLog.data?.events ?? []

  const fetchMembers = useFetchMembers({
    orgId,
    filter: {
      home_ids: [homeId],
    },
    options: {
      enabled: !!homeId,
    },
  })

  const members = fetchMembers.data?.members ?? []

  const columns: TableColumn<TTaskEvent>[] = [
    {
      value: "event",
      label: t(langKeys.event),
      enableSort: true,
      sortButtonSpacing: "compact",
      disableClickPropagation: true,
      renderCell: (data) => {
        return (
          <EventCell>
            <EventIcon performedByType={data.performed_by.type} />
            <Text variant="subtitle">
              {transformTaskEventName({
                t,
                eventType: data.event_type,
              })}
            </Text>
          </EventCell>
        )
      },
      columnWidth: "auto",
    },
    {
      value: "performed_by",
      label: t(langKeys.task_management_activity_log_performed_by),
      enableSort: true,
      sortButtonSpacing: "compact",
      renderCell: (data) => {
        return (
          <Text>
            {getMemberName({
              memberId:
                data.performed_by.type === "member"
                  ? data.performed_by.actor_id
                  : null,
              members,
            })}
          </Text>
        )
      },
      columnWidth: "auto",
    },

    {
      value: "time",
      label: t(langKeys.time),
      enableSort: true,
      sortButtonSpacing: "compact",
      renderCell: (data) => {
        return <LocalizedDate date={data.occurred_at} timezone={timezone} />
      },
      columnWidth: "auto",
    },
  ]

  const { rows, headerElements, templateColumns } = useTableColumns<TTaskEvent>(
    {
      columns,
      data: taskActivityLog,
    }
  )

  return (
    <div>
      <Text variant="heading2">{t(langKeys.task_management_task_events)}</Text>

      <Content>
        <GridTable
          header={headerElements}
          rows={rows}
          templateColumns={templateColumns}
          loading={fetchTaskActivityLog.isLoading}
          cellSize="small"
          useFallbackResponsiveMode
        />

        <Pager
          limit={limit}
          offset={offset}
          setOffset={setOffset}
          totalCount={fetchTaskActivityLog.data?.paging.total_count}
        />
      </Content>
    </div>
  )
}

function EventIcon({
  performedByType,
}: {
  performedByType: TTaskEvent["performed_by"]["type"]
}) {
  if (performedByType === "member") {
    return (
      <CircularIcon $backgroundColor={colors.primaryLight}>
        <ProfileIcon width={20} height={20} color={colors.textPrimary} />
      </CircularIcon>
    )
  }

  return (
    <CircularIcon $backgroundColor={colors.accentSecondary}>
      <MinutIcon width={12} height={8} color={colors.textPrimary} />
    </CircularIcon>
  )
}

function getMemberName({
  memberId,
  members,
}: {
  memberId: string | null
  members: IOrganizationMember[]
}) {
  return members.find((m) => m.member_id === memberId)?.name ?? "Minut"
}

function transformTaskEventName({
  t,
  eventType,
}: {
  t: TTranslateFunction
  eventType: TTaskEvent["event_type"]
}): string {
  const eventNameMap: Record<TTaskEvent["event_type"], string> = {
    created: t(langKeys.task_management_activity_log_task_created),
    updated_title: t(langKeys.task_management_activity_log_title_updated),
    updated_description: t(
      langKeys.task_management_activity_log_description_updated
    ),
    updated_assignee: t(langKeys.task_management_activity_log_assignee_edited),
    updated_status: t(langKeys.task_management_activity_log_status_updated),
    updated_assigned_to: t(
      langKeys.task_management_activity_log_assignee_edited
    ),
    updated_due_date: t(langKeys.task_management_activity_log_due_date_updated),
    deleted: t(langKeys.task_management_activity_log_task_deleted),
  }

  return eventNameMap[eventType]
}

const EventCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.XS};
`

const Content = styled.div`
  display: grid;
  gap: ${spacing.M};
`

const CircularIcon = styled.div<{ $backgroundColor: string }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.XS2};
`
